import { useGeolocationState } from 'contexts/GeolocationContext';
import React from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSearchParams } from '@/hooks/useSearchParams';

import { useProviderOutsideMatchesContext } from '../../contexts/ProviderOutsideMatchesContext';
import { useProviderSearchContext } from '../../contexts/ProviderSearchContext';
import { ProviderFacet } from './ProviderFacet';
import { ProviderFilterDesktop } from './ProviderFilterDesktop';
import { ProviderFilterMobile } from './ProviderFilterMobile';

const ProviderFilter = ({ ...props }) => {
    const breakpoint = useBreakpoint();
    const isMobile = !breakpoint?.large;

    const {
        coveo: {
            controller: { facet },
        },
        handleSingleFilter,
        handleFilterResetAll,
        sortCriteria,
        handleSortChange,
        computedSortLabel,
        disableNextAvailableSort,
    } = useProviderSearchContext();

    const geolocationState = useGeolocationState();
    const { isVpcOutsideMatches } = useProviderOutsideMatchesContext();

    const [specialtyState, setSpecialtyState] = React.useState();
    const [insuranceState, setInsuranceState] = React.useState();
    const [languageState, setLanguageState] = React.useState();
    const [genderState, setGenderState] = React.useState();
    const [typeState, setTypeState] = React.useState();
    const [, setVisitState] = React.useState();
    const [virtualPrimaryCareFacetState, setVirtualPrimaryCareFacetState] = React.useState();
    const [affiliationState, setAffiliationState] = React.useState();
    const [specialtyServiceState, setSpecialtyServiceState] = React.useState();

    React.useEffect(() => {
        const unsubscribe = facet.specialty.subscribe(() => {
            setSpecialtyState(facet.specialty.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.specialty]);

    React.useEffect(() => {
        const unsubscribe = facet.insurance.subscribe(() => {
            setInsuranceState(facet.insurance.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.insurance]);

    React.useEffect(() => {
        const unsubscribe = facet.language.subscribe(() => {
            setLanguageState(facet.language.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.language]);

    React.useEffect(() => {
        const unsubscribe = facet.gender.subscribe(() => {
            setGenderState(facet.gender.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.gender]);

    React.useEffect(() => {
        const unsubscribe = facet.type.subscribe(() => {
            setTypeState(facet.type.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.type]);

    React.useEffect(() => {
        const unsubscribe = facet.visit.subscribe(() => {
            setVisitState(facet.visit.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.visit]);

    React.useEffect(() => {
        const unsubscribe = facet.vpc.subscribe(() => {
            if (isVpcOutsideMatches) {
                return;
            }
            setVirtualPrimaryCareFacetState(facet.vpc.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.vpc, isVpcOutsideMatches]);

    React.useEffect(() => {
        const unsubscribe = facet.affiliation.subscribe(() => {
            setAffiliationState(facet.affiliation.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.affiliation]);

    React.useEffect(() => {
        const unsubscribe = facet.specialtyservice.subscribe(() => {
            setSpecialtyServiceState(facet.specialtyservice.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.specialtyservice]);

    const searchParams = useSearchParams();

    // Seed from the URL sort param so a direct link with ?sort=Next+Available renders correctly.
    // Subsequent changes go through handleSortChange, which writes ?sort= to the URL (v1.4.0).
    const [currentSortLabel, setCurrentSortLabel] = React.useState(
        sortCriteria.find(([name]) => name === searchParams.get('sort'))?.[0] ?? sortCriteria[0][0],
    );

    React.useEffect(() => {
        if (computedSortLabel && !searchParams.get('sort')) {
            setCurrentSortLabel(computedSortLabel);
        }
    }, [computedSortLabel, searchParams]);

    const handleSort = React.useCallback(
        (value) => {
            setCurrentSortLabel(value);
            handleSortChange(value);
        },
        [handleSortChange],
    );
    const disabledDistanceOptions =
        !searchParams.has('zip-code') &&
        !searchParams.has('city') &&
        !geolocationState?.recentSearch;
    const urlDistance = searchParams.get('distance') ?? '';

    const [distance, setDistance] = React.useState(urlDistance);
    React.useEffect(() => {
        setDistance(urlDistance);
    }, [urlDistance]);

    const accordionItems = React.useMemo(
        () => [
            ...(specialtyState?.values?.length > 0
                ? [
                      {
                          id: 'specialty',
                          title: 'Specialty',
                          titleIndicator: <ProviderFacet.Badge fieldName={'specialties'} />,
                          children: (
                              <ProviderFacet.Root
                                  urlParamName={'specialty'}
                                  variant={'searchable-checkbox'}
                                  controller={facet.specialty}
                                  analytics={{
                                      componentTitle: 'Specialty',
                                  }}
                                  fixedOption={'primary care'}
                              />
                          ),
                      },
                  ]
                : []),
            ...(insuranceState?.values?.length > 0
                ? [
                      {
                          id: 'insurance',
                          title: 'Insurance Carriers',
                          titleIndicator: <ProviderFacet.Badge fieldName={'insuranceaccepted'} />,
                          children: (
                              <ProviderFacet.Root
                                  urlParamName={'insurance'}
                                  variant={'searchable-checkbox'}
                                  controller={facet.insurance}
                                  title={
                                      'Please contact the practice to confirm your health plan is accepted.'
                                  }
                                  analytics={{
                                      componentTitle: 'Insurance',
                                  }}
                              />
                          ),
                      },
                  ]
                : []),
            ...(languageState?.values?.length > 0
                ? [
                      {
                          id: 'language',
                          title: 'Language',
                          titleIndicator: <ProviderFacet.Badge fieldName={'languages'} />,
                          children: (
                              <ProviderFacet.Root
                                  urlParamName={'language'}
                                  variant={'searchable-radio'}
                                  controller={facet.language}
                                  analytics={{
                                      componentTitle: 'Language',
                                  }}
                              />
                          ),
                      },
                  ]
                : []),
            ...(genderState?.values?.length > 0
                ? [
                      {
                          id: 'gender',
                          title: 'Gender',
                          titleIndicator: <ProviderFacet.Badge fieldName={'gender'} />,
                          children: (
                              <ProviderFacet.Root
                                  urlParamName={'gender'}
                                  variant={'checkbox'}
                                  controller={facet.gender}
                                  analytics={{
                                      componentTitle: 'Gender',
                                  }}
                              />
                          ),
                      },
                  ]
                : []),
            ...(typeState?.values?.length > 0
                ? [
                      {
                          id: 'type',
                          title: 'Provider Type',
                          titleIndicator: <ProviderFacet.Badge fieldName={'providertype'} />,
                          children: (
                              <ProviderFacet.Root
                                  urlParamName={'type'}
                                  variant={'checkbox'}
                                  controller={facet.type}
                                  analytics={{
                                      componentTitle: 'Provider Type',
                                  }}
                              />
                          ),
                      },
                  ]
                : []),
            ...(affiliationState?.values?.length > 0
                ? [
                      {
                          id: 'affiliation',
                          title: 'Affiliations',
                          titleIndicator: (
                              <ProviderFacet.Badge fieldName={'hospitalaffiliations'} />
                          ),
                          children: (
                              <ProviderFacet.Root
                                  urlParamName={'affiliation'}
                                  variant={'searchable-checkbox'}
                                  controller={facet.affiliation}
                                  analytics={{
                                      componentTitle: 'Affiliations',
                                  }}
                              />
                          ),
                      },
                  ]
                : []),
            ...(specialtyServiceState?.values?.length > 0
                ? [
                      {
                          id: 'specialtyservice',
                          title: 'Specialty Services',
                          titleIndicator: <ProviderFacet.Badge fieldName={'specialtyservice'} />,
                          children: (
                              <ProviderFacet.Root
                                  urlParamName={'specialtyservice'}
                                  variant={'checkbox'}
                                  controller={facet.specialtyservice}
                                  analytics={{
                                      componentTitle: 'Specialty Services',
                                  }}
                              />
                          ),
                      },
                  ]
                : []),
        ],
        [
            facet,
            specialtyState,
            insuranceState,
            languageState,
            genderState,
            typeState,
            affiliationState,
            specialtyServiceState,
        ],
    );

    const showVpcCheckbox = virtualPrimaryCareFacetState?.values?.some(
        (v) => v.value === 'True' && v.numberOfResults > 0,
    );

    return (
        <>
            {isMobile && (
                <ProviderFilterMobile
                    {...props}
                    accordionItems={accordionItems}
                    sortValue={currentSortLabel}
                    sortCriteria={sortCriteria}
                    handleSort={handleSort}
                    distance={distance}
                    setDistance={setDistance}
                    disabledDistanceOptions={disabledDistanceOptions}
                    handleSingleFilter={handleSingleFilter}
                    handleResetAll={handleFilterResetAll}
                    disableNextAvailableSort={disableNextAvailableSort}
                    showVpcCheckbox={showVpcCheckbox}
                />
            )}
            {!isMobile && (
                <ProviderFilterDesktop
                    {...props}
                    accordionItems={accordionItems}
                    sortValue={currentSortLabel}
                    sortCriteria={sortCriteria}
                    distance={distance}
                    setDistance={setDistance}
                    disabledDistanceOptions={disabledDistanceOptions}
                    handleSort={handleSort}
                    handleSingleFilter={handleSingleFilter}
                    handleResetAll={handleFilterResetAll}
                    disableNextAvailableSort={disableNextAvailableSort}
                    showVpcCheckbox={showVpcCheckbox}
                />
            )}
        </>
    );
};

export default ProviderFilter;
